import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { apiJson, assetUrl } from "../utils/api";
import useSeo from "../hooks/useSeo";
import RelationshipFeedbackForm from "../components/RelationshipFeedbackForm";

function MapLevel({ level }) {
  const map = {
    high: { text: "共识较高", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    medium: { text: "值得了解", cls: "bg-yellow-50 text-yellow-900 border-yellow-200" },
    watch: { text: "重点讨论", cls: "bg-amber-50 text-amber-900 border-amber-200" },
  };
  const m = map[level] || map.medium;
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${m.cls}`}>
      {m.text}
    </span>
  );
}

export default function RelationshipGrowthPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = Number(localStorage.getItem("user_id"));
  const targetFromQuery = searchParams.get("target");

  const [partners, setPartners] = useState([]);
  const [targetId, setTargetId] = useState(targetFromQuery || "");
  const [manualId, setManualId] = useState("");
  const [growth, setGrowth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [learned, setLearned] = useState([]);

  useSeo({
    title: "关系成长追踪｜看你们一起走过的关系实验",
    metaDescription:
      "根据单人与共同关系实验，生成关系地图、时间线与值得讨论的主题，帮助你们持续了解彼此。",
    path: "/ai/relationship-growth",
  });

  useEffect(() => {
    if (!userId) {
      navigate(
        `/login?redirect=${encodeURIComponent("/ai/relationship-growth")}`,
        { replace: true }
      );
    }
  }, [userId, navigate]);

  const loadPartners = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [pRes, fbRes] = await Promise.all([
        apiJson(
          `/api/ai/simulation/lab/growth/partners?user_id=${userId}&limit=20`
        ),
        apiJson(`/api/ai/relationship-feedback?user_id=${userId}&limit=5`),
      ]);
      if (pRes.res.ok) {
        const items = pRes.data.items || [];
        setPartners(items);
        if (!targetId && items[0]?.id) {
          setTargetId(String(items[0].id));
        }
      }
      if (fbRes.res.ok) {
        setLearned(
          (fbRes.data.items || [])
            .filter((x) => x.applied)
            .map((x) => ({
              id: x.id,
              summary: x.applied_summary,
            }))
        );
      }
    } catch (err) {
      setError(err.message || "加载失败");
    } finally {
      setLoading(false);
    }
  }, [userId, targetId]);

  useEffect(() => {
    loadPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (targetFromQuery) setTargetId(String(targetFromQuery));
  }, [targetFromQuery]);

  const loadGrowth = useCallback(async () => {
    const tid = Number(targetId || manualId);
    if (!userId || !tid) return;
    setBusy(true);
    setError("");
    try {
      const { res, data } = await apiJson(
        `/api/ai/simulation/lab/growth?user_id=${userId}&target_user_id=${tid}`
      );
      if (!res.ok) throw new Error(data?.detail || "加载成长追踪失败");
      setGrowth(data);
      setTargetId(String(tid));
      setSearchParams({ target: String(tid) }, { replace: true });
    } catch (err) {
      setError(err.message || "加载失败");
      setGrowth(null);
    } finally {
      setBusy(false);
    }
  }, [userId, targetId, manualId, setSearchParams]);

  useEffect(() => {
    if (targetId) loadGrowth();
  }, [targetId]); // eslint-disable-line react-hooks/exhaustive-deps

  const disclaimer =
    growth?.disclaimer ||
    "温馨提示：关系成长追踪基于已完成实验生成，仅用于帮助发现差异与共识，不代表对未来的预测。";

  return (
    <div className="min-h-screen bg-[#fbf7f2] flex flex-col">
      <Header />
      <main className="flex-1 w-full mx-auto px-4 py-8 max-w-5xl space-y-6">
        <section className="rounded-3xl bg-gradient-to-br from-rose-50 via-violet-50 to-sky-50 border border-white p-6 md:p-8 space-y-3">
          <p className="text-sm text-violet-600 font-medium">关系实验 · 成长追踪</p>
          <h1 className="text-3xl font-extrabold text-gray-900">关系成长追踪</h1>
          <p className="text-sm text-gray-600 max-w-xl leading-relaxed">
            看你们一起做过哪些关系实验、地图怎么变化、还有哪些值得提前聊的事。
          </p>
          <div className="flex flex-wrap gap-2 text-xs">
            <Link to="/ai/simulation" className="text-violet-700 hover:underline">
              关系模拟实验室
            </Link>
            <span className="text-gray-300">·</span>
            <Link to="/ai/behavior-profile" className="text-amber-800 hover:underline">
              关系行为画像
            </Link>
            <span className="text-gray-300">·</span>
            <Link to="/ai/relationship-memory" className="text-violet-700 hover:underline">
              关系记忆看板
            </Link>
            <span className="text-gray-300">·</span>
            <Link to="/ai/relationship-map" className="text-violet-700 hover:underline">
              AI 关系地图
            </Link>
            <span className="text-gray-300">·</span>
            <Link to="/ai/growth" className="text-violet-700 hover:underline">
              成长中心
            </Link>
          </div>
        </section>

        {learned.length > 0 && (
          <section className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4 space-y-2">
            <p className="text-sm font-semibold text-emerald-900">
              AI 已根据你的反馈更新了观察
            </p>
            <ul className="space-y-1">
              {learned.map((item) => (
                <li key={item.id} className="text-xs text-emerald-900/90">
                  · {item.summary}
                </li>
              ))}
            </ul>
          </section>
        )}

        <RelationshipFeedbackForm
          userId={userId}
          source="dating"
          targetUserId={targetId || null}
          compact
          onSubmitted={() => loadPartners()}
        />

        <section className="bg-white rounded-2xl border p-4 space-y-3">
          <h2 className="font-semibold text-gray-800 text-sm">选择追踪对象</h2>
          {loading ? (
            <p className="text-sm text-gray-400">加载中…</p>
          ) : partners.length === 0 ? (
            <p className="text-sm text-gray-500">
              还没有实验对象。去{" "}
              <Link to="/ai/simulation" className="text-rose-600">
                关系模拟实验室
              </Link>{" "}
              开始第一场实验。
            </p>
          ) : (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {partners.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setTargetId(String(p.id))}
                  className={`shrink-0 flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm ${
                    String(p.id) === String(targetId)
                      ? "border-violet-400 bg-violet-50"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <img
                    src={assetUrl(p.avatar) || "https://picsum.photos/32"}
                    alt=""
                    className="w-7 h-7 rounded-full object-cover"
                  />
                  {p.name}
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <input
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
              placeholder="或输入用户 ID"
              className="border rounded-xl px-3 py-2 text-sm flex-1 min-w-[140px]"
            />
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                if (manualId) setTargetId(String(manualId));
                else loadGrowth();
              }}
              className="px-4 py-2 rounded-full bg-violet-600 text-white text-sm disabled:opacity-50"
            >
              {busy ? "加载中…" : "查看追踪"}
            </button>
          </div>
        </section>

        {error && (
          <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-xl px-4 py-3">
            {error}
          </div>
        )}

        {growth && (
          <div className="space-y-5 animate-[fadeIn_0.3s_ease]">
            <section className="bg-white rounded-2xl border p-5 space-y-3">
              <div className="flex items-center gap-3">
                <img
                  src={
                    assetUrl(growth.partner?.avatar) || "https://picsum.photos/48"
                  }
                  alt=""
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-bold text-gray-900">
                    我 × {growth.partner?.name || "TA"}
                  </p>
                  <p className="text-sm text-violet-700">
                    成长阶段：{growth.growth_level}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                {[
                  ["单人完成", growth.stats?.solo_done],
                  ["共同完成", growth.stats?.joint_done],
                  ["方向接近题", growth.stats?.align_choices],
                  ["值得讨论题", growth.stats?.diff_choices],
                ].map(([label, val]) => (
                  <div key={label} className="rounded-xl bg-violet-50/80 px-2 py-3">
                    <p className="text-lg font-bold text-violet-800">{val ?? 0}</p>
                    <p className="text-[11px] text-gray-500">{label}</p>
                  </div>
                ))}
              </div>
            </section>

            {(growth.map_items || []).length > 0 && (
              <section className="bg-white rounded-2xl border p-5 space-y-3">
                <h3 className="font-semibold text-gray-800">关系地图</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {growth.map_items.map((it) => (
                    <div
                      key={it.key}
                      className="rounded-xl border px-3 py-2 flex flex-col gap-1"
                    >
                      <span className="text-xs text-gray-500">{it.label}</span>
                      <MapLevel level={it.level} />
                    </div>
                  ))}
                </div>
                {(growth.focus_dimensions || []).length > 0 && (
                  <p className="text-xs text-amber-800">
                    近期可多关注：{growth.focus_dimensions.join("、")}
                  </p>
                )}
              </section>
            )}

            {(growth.discuss_topics || []).length > 0 && (
              <section className="bg-amber-50/80 rounded-2xl border border-amber-100 p-5 space-y-2">
                <h3 className="font-semibold text-amber-900">最值得提前讨论</h3>
                <ul className="text-sm text-amber-950 space-y-1.5">
                  {growth.discuss_topics.map((t) => (
                    <li key={t}>· {t}</li>
                  ))}
                </ul>
              </section>
            )}

            {(growth.memory_notes || []).length > 0 && (
              <section className="bg-violet-50/60 rounded-2xl border border-violet-100 p-5 space-y-2">
                <h3 className="font-semibold text-violet-900">关系记忆摘要</h3>
                <ul className="text-xs text-violet-900 space-y-1.5">
                  {growth.memory_notes.map((n) => (
                    <li key={n}>· {n}</li>
                  ))}
                </ul>
              </section>
            )}

            <section className="bg-white rounded-2xl border p-5 space-y-3">
              <h3 className="font-semibold text-gray-800">实验时间线</h3>
              {(growth.timeline || []).length === 0 ? (
                <p className="text-sm text-gray-500">还没有实验记录。</p>
              ) : (
                <div className="space-y-2">
                  {growth.timeline.map((ev) => (
                    <div
                      key={`${ev.type}-${ev.id}`}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-sm"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-gray-800 truncate">
                          {ev.type === "joint" ? "🤝" : "🧪"} {ev.title}
                          {ev.custom ? " · 自定义题" : ""}
                        </p>
                        <p className="text-[11px] text-gray-400">
                          {ev.status === "done" ? "已完成" : ev.status || "进行中"}
                          {ev.at ? ` · ${String(ev.at).slice(0, 16).replace("T", " ")}` : ""}
                        </p>
                        {ev.summary && (
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                            {ev.summary}
                          </p>
                        )}
                      </div>
                      {ev.type === "joint" && ev.token && (
                        <Link
                          to={`/ai/simulation?joint=${ev.token}`}
                          className="text-xs text-violet-700 border border-violet-200 rounded-full px-3 py-1"
                        >
                          打开
                        </Link>
                      )}
                      {ev.type === "solo" && (
                        <Link
                          to={`/ai/simulation?target=${targetId}`}
                          className="text-xs text-gray-600 border rounded-full px-3 py-1"
                        >
                          去实验室
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="bg-white rounded-2xl border p-5 space-y-2">
              <h3 className="font-semibold text-gray-800">下一步建议</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                {(growth.suggestions || []).map((s) => (
                  <li key={s}>· {s}</li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-2 pt-2">
                <Link
                  to={`/ai/simulation?target=${targetId}`}
                  className="px-4 py-2 rounded-full bg-violet-600 text-white text-sm"
                >
                  继续关系实验
                </Link>
                <Link
                  to={`/user/${targetId}`}
                  className="px-4 py-2 rounded-full border border-rose-200 text-rose-600 text-sm"
                >
                  查看 TA 资料
                </Link>
              </div>
            </section>

            <p className="text-[11px] text-gray-400 leading-relaxed">{disclaimer}</p>
            <p className="text-[11px] text-gray-400">{growth.note}</p>
          </div>
        )}
      </main>
      <Footer />
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
